import { apiClient } from './api-client';
import { CustomerResponse } from '../dto/customer/CustomerResponse';
import { ErrorResponse } from '../dto/common/ErrorResponse';

/**
 * Customer service.
 * Handles customer CRUD operations.
 */
class CustomerService {
  /**
   * Gets all customers.
   * @returns {Promise<{success: boolean, data?: CustomerResponse[], error?: ErrorResponse}>}
   */
  async getCustomers() {
    try {
      const response = await apiClient.get('/customers');
      const customers = response.data.map(item => CustomerResponse.fromJSON(item));
      return { success: true, data: customers };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Creates a new customer.
   * @param {CreateCustomerRequest} request - Customer creation data
   * @returns {Promise<{success: boolean, data?: CustomerResponse, error?: ErrorResponse}>}
   */
  async createCustomer(request) {
    try {
      const response = await apiClient.post('/customers', request.toJSON());
      const customer = CustomerResponse.fromJSON(response.data);
      return { success: true, data: customer };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Updates an existing customer.
   * @param {string} customerId - Customer UUID
   * @param {UpdateCustomerRequest} request - Customer update data
   * @returns {Promise<{success: boolean, data?: CustomerResponse, error?: ErrorResponse}>}
   */
  async updateCustomer(customerId, request) {
    try {
      const response = await apiClient.put(`/customers/${customerId}`, request.toJSON());
      const customer = CustomerResponse.fromJSON(response.data);
      return { success: true, data: customer };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Deletes a customer.
   * @param {string} customerId - Customer UUID
   * @returns {Promise<{success: boolean, error?: ErrorResponse}>}
   */
  async deleteCustomer(customerId) {
    try {
      await apiClient.delete(`/customers/${customerId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }
}

export default new CustomerService();
